/**
 * SellerInboxTab — buyer↔seller per-store chat inbox.
 * Self-contained: owns its own state, polls /seller/inbox every 15s,
 * marks conversations read on selection. Extracted from SellerPage.jsx
 * in the iter-41 component-split sprint.
 */
import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

export default function SellerInboxTab() {
  const [convs, setConvs] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadList = async () => {
    try {
      const { data } = await api.get("/seller/inbox");
      setConvs(data.conversations || []);
    } finally { setLoading(false); }
  };

  const loadActive = async (conv) => {
    if (!conv) return;
    try {
      const { data } = await api.get(`/seller/inbox/${conv.buyer_id}`,
        { params: { store_id: conv.store_id } });
      const full = (data.conversations || []).find(c => c.conv_id === conv.conv_id)
                   || data.conversations?.[0];
      setActive(full);
      loadList();
    } catch { /* ignore */ }
  };

  useEffect(() => { loadList(); const i = setInterval(loadList, 15000); return () => clearInterval(i); }, []);
  useEffect(() => {
    if (!activeConvId) { setActive(null); return; }
    const conv = convs.find(c => c.conv_id === activeConvId);
    if (conv) loadActive(conv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConvId, convs.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!reply.trim() || !active || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/seller/inbox/reply",
        { buyer_id: active.buyer_id, text: reply.trim() },
        { params: { store_id: active.store_id } });
      setReply("");
      setActive((c) => c ? { ...c, messages: [...(c.messages || []), data.message] } : c);
      loadList();
    } catch (ex) {
      alert(formatApiError(ex?.response?.data?.detail) || "Send failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="card p-8 text-center text-[#A1A1AA]">Loading inbox…</div>;

  if (!convs.length) {
    return (
      <div className="card p-10 text-center" data-testid="seller-inbox-empty">
        <MessageCircle size={36} className="mx-auto text-[#52525B] mb-3"/>
        <div className="font-display font-bold text-xl mb-1">No messages yet</div>
        <div className="text-sm text-[#A1A1AA]">When buyers chat with you on a store page, conversations will land here.</div>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4 min-h-[60vh]">
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-[#3D2845] bg-[#0F0F11]">
          <div className="font-display font-black text-lg uppercase">Inbox</div>
          <div className="text-[10px] text-[#A1A1AA]">{convs.length} conversation{convs.length === 1 ? "" : "s"}</div>
        </div>
        <div className="divide-y divide-[#3D2845] max-h-[60vh] overflow-y-auto" data-testid="inbox-conv-list">
          {convs.map(c => {
            const last = c.last_message;
            const isActive = c.conv_id === activeConvId;
            return (
              <button key={c.conv_id} onClick={() => setActiveConvId(c.conv_id)}
                data-testid={`inbox-conv-${c.buyer_id}`}
                className={`w-full text-left p-3 hover:bg-[#3D2845]/40 transition ${isActive ? "bg-[#3D2845]/60" : ""}`}>
                <div className="flex items-center gap-3">
                  {c.buyer_picture
                    ? <img src={c.buyer_picture} className="w-10 h-10 rounded-full" alt=""/>
                    : <div className="w-10 h-10 rounded-full bg-[#3D2845] flex items-center justify-center font-bold text-[#A1A1AA]">{(c.buyer_name || "?")[0]}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-sm truncate">{c.buyer_name || "Buyer"}</div>
                      {c.seller_unread > 0 && (
                        <span className="shrink-0 min-w-[18px] h-4 px-1.5 rounded-full bg-[#FF007F] text-white text-[10px] font-bold flex items-center justify-center"
                          data-testid={`inbox-unread-${c.buyer_id}`}>{c.seller_unread}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#A1A1AA] truncate">{c.store_name}</div>
                    {last && <div className="text-[11px] text-[#A1A1AA] truncate mt-0.5">
                      {last.from_role === "seller" ? "You: " : ""}{last.text}
                    </div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="card overflow-hidden flex flex-col" data-testid="inbox-pane">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
            <MessageCircle size={36} className="text-[#52525B] mb-3"/>
            <div className="text-sm text-[#A1A1AA]">Select a conversation to view and reply.</div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-[#3D2845] bg-[#0F0F11] flex items-center gap-3">
              {active.buyer_picture && <img src={active.buyer_picture} className="w-9 h-9 rounded-full" alt=""/>}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{active.buyer_name || "Buyer"}</div>
                <div className="text-[10px] text-[#A1A1AA]">via {active.store_name}</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#0F0817]"
              data-testid="inbox-messages">
              {(active.messages || []).map(m => {
                const mine = m.from_role === "seller";
                const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
                      mine ? "bg-[#D4FF00] text-black" : "bg-[#3D2845] text-[#FAFAFA]"
                    }`}>
                      <div className="whitespace-pre-line break-words">{m.text}</div>
                      <div className={`text-[9px] mt-1 ${mine ? "text-black/60" : "text-[#A1A1AA]"} text-right`}>{time}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-3 border-t border-[#3D2845] bg-[#0F0F11] flex gap-2">
              <input value={reply} onChange={(e) => setReply(e.target.value)}
                placeholder="Reply to buyer…" maxLength={2000}
                data-testid="inbox-reply-input"
                className="input-dark flex-1 px-3 py-2 text-sm"/>
              <button type="submit" disabled={busy || !reply.trim()}
                data-testid="inbox-reply-send"
                className="btn-primary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
                <Send size={14}/>
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
